import { useMemo } from 'react';

import {
  TableCell,
  TableRow,
  StatusChip,
  Typography,
  TableRowActionsMenu,
  TableRowActionsMenuItem,
} from '@occc-app/design-system';
import { separateEveryThousandByComma } from '@occc-app/lib/numbers';
import { Feature, useIsUserPermittedTo } from '@occc/utils-access-control';
import { ScanDataFileStatusType } from '@occc/graphql';

import { SubmissionGroupData } from '../../scan-data-weekly-file/scan-data-weekly-file.types';
import {
  fileStatusToColorMap,
  fileStatusToLabelMap,
  pastSubmissionsTypeToLabelMap,
} from '../../scan-data-weekly-file/scan-data-weekly-file.constants';

type ScanDataPastSubmissionTableRowProps = {
  data: SubmissionGroupData;
  onResubmitClick: () => void;
};

export const ScanDataPastSubmissionTableRow = ({
  data,
  onResubmitClick,
}: ScanDataPastSubmissionTableRowProps) => {
  const isSubmitPermitted = useIsUserPermittedTo(Feature.SCAN_DATA_SUBMIT_WEEKLY_FILE);

  const isResubmitAllowed =
    isSubmitPermitted &&
    (data.fileStatus === ScanDataFileStatusType.Submitted ||
      data.fileStatus === ScanDataFileStatusType.PartiallySubmitted);

  const menuItems: TableRowActionsMenuItem[] = useMemo(() => {
    const items: TableRowActionsMenuItem[] = [];

    if (isResubmitAllowed && data.actionsAvailable) {
      items.push({
        label: 'Resubmit Existing File',
        onClick: onResubmitClick,
        dataTestId: 'resubmit-existing-file',
      });
    }

    return items;
  }, [isResubmitAllowed, data.actionsAvailable, onResubmitClick]);

  return (
    <TableRow>
      <TableCell>{data.name}</TableCell>
      <TableCell>{pastSubmissionsTypeToLabelMap[data.type]}</TableCell>
      <TableCell>
        <Typography variant='body2'>
          {separateEveryThousandByComma(data.storesSubmittedCount)}/
          {separateEveryThousandByComma(data.totalStoresCount)} Stores Submitted
        </Typography>
      </TableCell>
      <TableCell>
        <StatusChip
          label={fileStatusToLabelMap[data.fileStatus]}
          variant={fileStatusToColorMap[data.fileStatus]}
        />
      </TableCell>
      <TableCell>
        {data.isResubmitted ? (
          <StatusChip label='Yes' variant='error' />
        ) : (
          <StatusChip label='No' variant='success' />
        )}
      </TableCell>
      <TableCell align='right'>
        {menuItems.length > 0 && <TableRowActionsMenu menuItems={menuItems} />}
      </TableCell>
    </TableRow>
  );
};
